import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { AlertCircle, CheckCircle2, ExternalLink, Loader2, MessageCircle } from "lucide-react"
import { addDoc, collection, serverTimestamp } from "firebase/firestore"
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth"
import { database, FIREBASE_AUTH } from "@/firebase"
import { useAuthState } from "@/context/useAuthState"
import type { Vehicle } from "@/types"

const WHATSAPP_NUMBER = import.meta.env.VITE_WHATSAPP_NUMBER ?? "18092468383"

function buildWhatsAppUrl(phone: string, text: string) {
  const clean = phone.replace(/\D/g, "")
  return `https://wa.me/${clean}?text=${encodeURIComponent(text)}`
}

const formatPrice = (price?: number | null) =>
  Number.isFinite(price) ? (price as number).toLocaleString() : "0"

interface FinancingRequestDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vehicle: Vehicle
}

type Status = "idle" | "loading" | "success" | "error"

export function FinancingRequestDialog({
  open,
  onOpenChange,
  vehicle,
}: FinancingRequestDialogProps) {
  const { user, setUser } = useAuthState()
  const [status, setStatus] = useState<Status>("idle")
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const price = vehicle.salePrice ?? 0
  const brandName = vehicle.brand?.name ?? null

  const handleClose = (next: boolean) => {
    if (!next) {
      setStatus("idle")
      setErrorMessage(null)
    }
    onOpenChange(next)
  }

  const handleWhatsApp = () => {
    const vehicleUrl = window.location.href
    const yearPart = vehicle.year ? ` ${vehicle.year}` : ""
    const message =
      `Hola, me interesa financiar el *${vehicle.name}${yearPart}* ` +
      `(RD$ ${formatPrice(price)}). Link: ${vehicleUrl}`
    window.open(
      buildWhatsAppUrl(WHATSAPP_NUMBER, message),
      "_blank",
      "noopener,noreferrer",
    )
  }

  const handleOnlineRequest = async () => {
    setStatus("loading")
    setErrorMessage(null)
    try {
      let currentUser = user
      if (!currentUser) {
        const credential = await signInWithPopup(
          FIREBASE_AUTH,
          new GoogleAuthProvider(),
        )
        currentUser = credential.user
        setUser(credential.user)
      }

      await addDoc(collection(database, "quote_requests"), {
        type: "financing",
        status: "pending",
        vehicleId: vehicle.id,
        vehicleName: vehicle.name,
        vehicleBrand: brandName,
        vehiclePrice: price,
        vehicleUrl: window.location.href,
        userId: currentUser.uid,
        userName: currentUser.displayName,
        userEmail: currentUser.email,
        userPhoto: currentUser.photoURL,
        createdAt: serverTimestamp(),
      })

      setStatus("success")
    } catch (err) {
      const code =
        err && typeof err === "object" && "code" in err
          ? String((err as { code: unknown }).code)
          : ""
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        setStatus("idle")
        return
      }
      console.error("Error al crear solicitud de financiamiento:", err)
      setErrorMessage(
        "No pudimos enviar tu solicitud. Por favor intenta de nuevo o usa WhatsApp.",
      )
      setStatus("error")
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#1F2240]">
            Solicitar Financiamiento
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {vehicle.name}
            {brandName ? ` · ${brandName}` : ""} — RD$ {formatPrice(price)}
          </DialogDescription>
        </DialogHeader>

        {status === "success" ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <CheckCircle2 className="h-14 w-14 text-[#25D366]" />
            <div>
              <h3 className="text-lg font-bold text-[#1F2240]">
                ¡Solicitud enviada!
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Recibimos tu solicitud de financiamiento. Nuestro equipo te
                contactará pronto.
              </p>
            </div>
            <Button
              className="w-full bg-[#1F2240] text-white hover:bg-[#2A2E54]"
              onClick={() => handleClose(false)}
            >
              Cerrar
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-1">
            <p className="text-sm text-gray-600">
              Elige cómo deseas continuar con tu solicitud:
            </p>

            <Button
              type="button"
              onClick={handleWhatsApp}
              disabled={status === "loading"}
              className="h-12 w-full justify-center gap-2 bg-[#25D366] text-white hover:bg-[#20BD5A]"
            >
              <MessageCircle className="h-5 w-5" />
              Contactar por WhatsApp
              <ExternalLink className="h-4 w-4" />
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-400">o</span>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleOnlineRequest}
              disabled={status === "loading"}
              className="h-12 w-full justify-center gap-2 bg-[#E10F18] text-white hover:bg-[#C40D17]"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Enviando…
                </>
              ) : (
                "Solicitar en línea"
              )}
            </Button>
            {!user && (
              <p className="text-center text-xs text-gray-400">
                Te pediremos iniciar sesión con Google para registrar tu
                solicitud.
              </p>
            )}

            {status === "error" && errorMessage && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
